import { JsPsych, JsPsychPlugin, ParameterType, TrialType } from "jspsych";
import {
  INSTRUCTIONS_AUDIO_ID,
  resolveLayoutPlaceholders,
} from "../lib/utils";
import { showToastMessage } from "../lib/toast";

const info = <const>{
  name: "instructions",
  parameters: {
    /** Each element of the array is the content for a single page. Each page should be an HTML-formatted string.  */
    pages: {
      type: ParameterType.HTML_STRING,
      default: undefined,
      array: true,
    },
    /** This is the key that the participant can press in order to advance to the next page. This key should be
     * specified as a string (e.g., `'a'`, `'ArrowLeft'`, `' '`, `'Enter'`). */
    key_forward: {
      type: ParameterType.KEY,
      default: "ArrowRight",
    },
    /** This is the key that the participant can press to return to the previous page. This key should be specified as a
     * string (e.g., `'a'`, `'ArrowLeft'`, `' '`, `'Enter'`). */
    key_backward: {
      type: ParameterType.KEY,
      default: "ArrowLeft",
    },
    /** If true, the participant can return to previous pages of the instructions. If false, they may only advace to the next page. */
    allow_backward: {
      type: ParameterType.BOOL,
      default: true,
    },
    /** If `true`, the participant can use keyboard keys to navigate the pages. If `false`, they may not. */
    allow_keys: {
      type: ParameterType.BOOL,
      default: true,
    },
    /** If true, then a `Previous` and `Next` button will be displayed beneath the instructions. Participants can
     * click the buttons to navigate. */
    show_clickable_nav: {
      type: ParameterType.BOOL,
      default: false,
    },
    /** If true, and clickable navigation is enabled, then Page x/y will be shown between the nav buttons. */
    show_page_number: {
      type: ParameterType.BOOL,
      default: false,
    },
    /** The text that appears before x/y pages displayed when show_page_number is true.*/
    page_label: {
      type: ParameterType.STRING,
      default: "Page",
    },
    /** If provided, override the total number shown for page numbering (for multiple trials) */
    total_pages: {
      type: ParameterType.INT,
      default: undefined,
    },
    /** If provided, offset to add to current page number (useful to continue numbering across trials) */
    page_offset: {
      type: ParameterType.INT,
      default: 0,
    },
    /** The text that appears on the button to go backwards. */
    button_label_previous: {
      type: ParameterType.STRING,
      default: "Previous",
    },
    /** The text that appears on the button to go forwards. */
    button_label_next: {
      type: ParameterType.STRING,
      default: "Next",
    },
    /** The callback function when page changes */
    on_page_change: {
      type: ParameterType.FUNCTION,
      pretty_name: "Page change callback",
      default: function (current_page: number) {},
    },
    /** Optional looping ambience audio to play throughout the instructions trial. */
    ambient_audio_src: {
      type: ParameterType.STRING,
      default: null,
    },
    /** Whether the ambience audio should loop. */
    ambient_audio_loop: {
      type: ParameterType.BOOL,
      default: true,
    },
    /** If true, show a centered overlay text on top of the instruction image. */
    show_overlay_nav_text: {
      type: ParameterType.BOOL,
      default: false,
    },
    /** The overlay text displayed over the instruction image when enabled. */
    overlay_nav_text: {
      type: ParameterType.STRING,
      default: null,
    },
    /** If true, show the instruction review tip and enable review-forward behavior after reaching the final page once. */
    enable_instructions_review_tip: {
      type: ParameterType.BOOL,
      default: false,
    },
  },
  data: {
    /** An array containing the order of pages the participant viewed (including when the participant returned to previous pages)
     *  and the time spent viewing each page. Each object in the array represents a single page view,
     * and contains keys called `page_index` (the page number, starting with 0) and `viewing_time`
     * (duration of the page view). This will be encoded as a JSON string when data is saved using the `.json()` or `.csv()`
     * functions.
     */
    view_history: {
      type: ParameterType.COMPLEX,
      array: true,
      nested: {
        page_index: {
          type: ParameterType.INT,
        },
        viewing_time: {
          type: ParameterType.INT,
        },
      },
    },
    /** The response time in milliseconds for the participant to view all of the pages. */
    rt: {
      type: ParameterType.INT,
    },
  },
  // prettier-ignore
  citations: '__CITATIONS__',
};

type Info = typeof info;
/**
 * This plugin is for showing instructions to the participant. It allows participants to navigate through multiple pages
 * of instructions at their own pace, recording how long the participant spends on each page. Navigation can be done using
 *  the mouse or keyboard. participants can be allowed to navigate forwards and backwards through pages, if desired.
 *
 * @author Josh de Leeuw
 * @see {@link https://www.jspsych.org/latest/plugins/instructions/ instructions plugin documentation on jspsych.org}
 */
class CustomInstructionsResponsePlugin implements JsPsychPlugin<Info> {
  static info = info;

  constructor(private jsPsych) {}

  trial(display_element: HTMLElement, trial: TrialType<Info>) {
    const jsPsychInstance = this.jsPsych;
    var current_page = 0;
    var view_history = [];
    var start_time = performance.now();
    var last_page_update_time = start_time;
    let audioIsPlaying = false;
    let reviewingInstructions = false;
    let reviewToastShown = false;
    let dismissReviewToast: (() => void) | null = null;
    // use keyboard value 'KeyP' so it matches the customKeyboardResponses code
    const SECRET_SKIP_KEY = "KeyP";
    // Track the current audio element and handlers so we can attach/detach listeners across pages to skip any time we want
    let currentAudioEl: HTMLMediaElement | null = null;
    let audioPlayHandler: ((e?: any) => void) | null = null;
    let audioPauseHandler: ((e?: any) => void) | null = null;
    let audioEndedHandler: ((e?: any) => void) | null = null;
    let ambientAudioCtx: AudioContext | null = null;
    let ambientAudioSource: AudioBufferSourceNode | null = null;

    function btnListener() {
      if (this.id === "jspsych-instructions-back") {
        back();
      } else if (this.id === "jspsych-instructions-next") {
        next();
      }
    }

    function show_current_page() {
      if (trial.ambient_audio_src && !ambientAudioCtx) {
        (async () => {
          try {
            ambientAudioCtx = new AudioContext();
            const response = await fetch(trial.ambient_audio_src);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await ambientAudioCtx.decodeAudioData(arrayBuffer);
            ambientAudioSource = ambientAudioCtx.createBufferSource();
            ambientAudioSource.buffer = audioBuffer;
            ambientAudioSource.loop = trial.ambient_audio_loop ?? true;
            ambientAudioSource.connect(ambientAudioCtx.destination);
            ambientAudioSource.start(0);
          } catch (e) {
            // Ignore ambient audio errors (e.g. autoplay blocked).
          }
        })();
      }

      const finalPageCount =
        typeof trial.total_pages === "number" ? trial.total_pages : trial.pages.length;
      const currentDisplayedPage = (trial.page_offset || 0) + (current_page + 1);
      const isLastDisplayedInstructionPage = currentDisplayedPage === finalPageCount;

      const pageEntry = (trial.pages as any[])[current_page];
      var html = typeof pageEntry === "function" ? pageEntry() : pageEntry;

      var pagenum_display = "";
      if (trial.show_page_number) {
        var total_pages =
          typeof trial.total_pages === "number" ? trial.total_pages : trial.pages.length;
        var display_current = (trial.page_offset || 0) + (current_page + 1);
        pagenum_display =
          "<span style='margin: 0 1em;' class='" +
          "jspsych-instructions-pagenum'>" +
          (trial.page_label || "Page") +
          " " +
          display_current +
          "/" +
          total_pages +
          "</span>";
      }

      if (trial.show_clickable_nav) {
        var nav_html = "<div class='jspsych-instructions-nav' style='padding: 10px 0px;'>";
        if (trial.allow_backward) {
          var allowed = current_page > 0 ? "" : "disabled='disabled'";
          nav_html +=
            "<button id='jspsych-instructions-back' class='jspsych-btn' style='margin-right: 5px;' " +
            allowed +
            ">&lt; " +
            trial.button_label_previous +
            "</button>";
        }
        var total_pages_check =
          typeof trial.total_pages === "number" ? trial.total_pages : trial.pages.length;
        if (total_pages_check > 1 && trial.show_page_number) {
          nav_html += pagenum_display;
        }
        nav_html +=
          "<button id='jspsych-instructions-next' class='jspsych-btn'" +
          "style='margin-left: 5px;'>" +
          trial.button_label_next +
          " &gt;</button></div>";

        html += nav_html;
        display_element.innerHTML = html;
        if (current_page != 0 && trial.allow_backward) {
          display_element
            .querySelector("#jspsych-instructions-back")
            .addEventListener("click", btnListener, { once: true });
        }

        display_element
          .querySelector("#jspsych-instructions-next")
          .addEventListener("click", btnListener, { once: true });
      } else {
        var total_pages_check2 =
          typeof trial.total_pages === "number" ? trial.total_pages : trial.pages.length;
        if (trial.show_page_number && total_pages_check2 > 1) {
          // page numbers for non-mouse navigation
          html += "<div class='jspsych-instructions-pagenum'>" + pagenum_display + "</div>";
        }
        display_element.innerHTML = html;
      }

      // Inject a centered text on the instruction image when enabled.
      if (trial.show_overlay_nav_text) {
        const instructionImage = display_element.querySelector("#instructions_image") as HTMLElement | null;
        const imageParent = instructionImage?.parentElement;
        if (instructionImage && imageParent) {
          const existingOverlay = imageParent.querySelector("#jspsych-instructions-overlay-nav");
          if (!existingOverlay) {
            if (getComputedStyle(imageParent).position === "static") {
              imageParent.style.position = "relative";
            }

            const parseAttributeNumber = (attributeName: string, fallback: number) => {
              const rawValue = imageParent.getAttribute(attributeName);
              if (rawValue === null) return fallback;
              const parsed = Number(rawValue);
              return Number.isFinite(parsed) ? parsed : fallback;
            };

            const parseAttributeBoolean = (attributeName: string, fallback: boolean) => {
              const rawValue = imageParent.getAttribute(attributeName);
              if (rawValue === null) return fallback;
              if (rawValue === "true") return true;
              if (rawValue === "false") return false;
              return fallback;
            };

            const highlightKeyLabels = (text: string) =>
              resolveLayoutPlaceholders(jsPsychInstance, text).replace(
                /"([^"]+)"/g,
                '<span style="color:#DE5700;">"$1"</span>'
              );

            const overlayEl = document.createElement("div");
            overlayEl.id = "jspsych-instructions-overlay-nav";
            const overlayNavText =
              imageParent.getAttribute("data-overlay-nav-text") || trial.overlay_nav_text;
            const showBackKey = parseAttributeBoolean("data-overlay-show-back-key", true);
            const showForwardKey = parseAttributeBoolean("data-overlay-show-forward-key", true);
            const leftArrowSrc = "assets/images/instructions/leftInstructionArrow.svg";
            const rightArrowSrc = "assets/images/instructions/rightInstructionArrow.svg";
            const arrowMultiplier = 1;
            const textSizeVw = parseAttributeNumber("data-overlay-text-size-vw", 0.9);
            const arrowOffsetVw = 0;
            const sectionGapVw = 2.8;
            const overlayLeftPercent = parseAttributeNumber(
              "data-overlay-left-percent",
              51
            );
            const overlayTopPercent = parseAttributeNumber(
              "data-overlay-top-percent",
              88.5
            );
            const arrowSizeVw = textSizeVw * arrowMultiplier;
            const bubbleEl = imageParent.querySelector('[data-instructions-bubble="true"]') as HTMLElement | null;

            const buildOverlaySection = (content: string, alignment: "flex-start" | "center" | "flex-end") => `
              <span
                style="display:flex;align-items:center;justify-content:${alignment};flex:1 1 0;min-width:0;"
              >
                ${content}
              </span>
            `;

            const overlayParts: string[] = [];
            if (overlayNavText && showBackKey && showForwardKey) {
              overlayParts.push(
                buildOverlaySection(
                  `
                    <img
                      src="${leftArrowSrc}"
                      alt="Left arrow"
                      style="height:${arrowSizeVw}vw; width:auto; transform: translateY(${arrowOffsetVw}vw);"
                    />
                  `,
                  "flex-start"
                )
              );
              overlayParts.push(
                buildOverlaySection(
                  `<span class="jspsych-instructions-overlay-text" style="text-align:center;">${highlightKeyLabels(overlayNavText)}</span>`,
                  "center"
                )
              );
              overlayParts.push(
                buildOverlaySection(
                  `
                    <img
                      src="${rightArrowSrc}"
                      alt="Right arrow"
                      style="height:${arrowSizeVw}vw; width:auto; transform: translateY(${arrowOffsetVw}vw);"
                    />
                  `,
                  "flex-end"
                )
              );
            } else {
              if (showBackKey) {
                overlayParts.push(
                  buildOverlaySection(
                    `
                      <span style="display:flex; align-items:center; line-height:1; gap:-0.45vw;">
                        <img
                          src="${leftArrowSrc}"
                          alt="Left arrow"
                          style="height:${arrowSizeVw}vw; width:auto; transform: translateY(${arrowOffsetVw}vw);"
                        />
                        <span class="jspsych-instructions-overlay-text">${highlightKeyLabels(' [ press "__BACKWARD_KEY__" to go back ]')}</span>
                      </span>
                    `,
                    "flex-start"
                  )
                );
              }
              if (showBackKey && showForwardKey) {
                overlayParts.push(`<span aria-hidden="true" style="display:inline-block; flex:0 0 ${sectionGapVw}vw;"></span>`);
              }
              if (showForwardKey) {
                overlayParts.push(
                  buildOverlaySection(
                    `
                      <span style="display:flex; align-items:center; line-height:1; gap:-0.45vw;">
                        <span class="jspsych-instructions-overlay-text">${highlightKeyLabels('[ press "__FORWARD_KEY__" to continue ] ')}</span>
                        <img
                          src="${rightArrowSrc}"
                          alt="Right arrow"
                          style="height:${arrowSizeVw}vw; width:auto; transform: translateY(${arrowOffsetVw}vw);"
                        />
                      </span>
                    `,
                    showBackKey ? "flex-end" : "flex-end"
                  )
                );
              }
            }

            overlayEl.innerHTML = overlayParts.join("");
            
            overlayEl.style.position = "absolute";
            overlayEl.style.left = `${overlayLeftPercent}%`;
            overlayEl.style.top = `${overlayTopPercent}%`;
            overlayEl.style.transform = "translate(-50%, -50%)";
            overlayEl.style.zIndex = "20";
            overlayEl.style.pointerEvents = "none";
            overlayEl.style.display = "flex";
            overlayEl.style.alignItems = "center";
            overlayEl.style.justifyContent = showBackKey && showForwardKey ? "space-between" : showBackKey ? "flex-start" : "flex-end";
            overlayEl.style.gap = "0.6vw";
            overlayEl.style.whiteSpace = "pre";
            overlayEl.style.fontFamily = '"Inter", "Segoe UI", Arial, sans-serif';
            overlayEl.style.fontWeight = "700";
            overlayEl.style.fontSize = `${textSizeVw}vw`;
            overlayEl.style.color = "#828282";
            overlayEl.style.lineHeight = "1.2";

            // Prefer DOM anchoring to geometry math: place nav directly under the bubble when present.
            if (bubbleEl) {
              overlayEl.style.left = "50%";
              overlayEl.style.top = "calc(100% - 1.4vw)";
              overlayEl.style.transform = "translateX(-50%)";
              overlayEl.style.width = "100%";
              overlayEl.style.maxWidth = "100%";
              overlayEl.style.boxSizing = "border-box";
              overlayEl.style.paddingLeft = "1.6vw";
              overlayEl.style.paddingRight = "1.6vw";
              bubbleEl.appendChild(overlayEl);
            } else {
              imageParent.appendChild(overlayEl);
            }
          }
        }
      }

      // NOTE @RobertGemmaJr: This must be done AFTER all of the HTML is set
      // remove listeners from previous audio element if still there
      if (currentAudioEl) {
        if (audioPlayHandler) currentAudioEl.removeEventListener("play", audioPlayHandler);
        if (audioPauseHandler) currentAudioEl.removeEventListener("pause", audioPauseHandler);
        if (audioEndedHandler) currentAudioEl.removeEventListener("ended", audioEndedHandler);
        currentAudioEl = null;
        audioPlayHandler = audioPauseHandler = audioEndedHandler = null;
      }

      // Look for audio element with specific ID
      const foundAudio = document.getElementById(INSTRUCTIONS_AUDIO_ID) as HTMLMediaElement | null;
      if (foundAudio) {
        currentAudioEl = foundAudio; // remember the element so we can attach/detach listeners
        
        audioPlayHandler = () => {
          audioIsPlaying = true;
        };
        audioPauseHandler = () => {
          audioIsPlaying = false;
        };
        audioEndedHandler = () => {
          audioIsPlaying = false;
        };
        // attach listeners
        currentAudioEl.addEventListener("play", audioPlayHandler);
        currentAudioEl.addEventListener("pause", audioPauseHandler);
        currentAudioEl.addEventListener("ended", audioEndedHandler);

        // set initial state based on current playback
        audioIsPlaying = !currentAudioEl.paused && !currentAudioEl.ended;
      } else {
        audioIsPlaying = false;
      }

      // Enable review tip/skip mode only when the trial explicitly opts in.
      if (trial.enable_instructions_review_tip && isLastDisplayedInstructionPage && currentAudioEl) {
        if (!reviewToastShown) {
          dismissReviewToast = showToastMessage(
            resolveLayoutPlaceholders(
              jsPsychInstance,
              "Tip: you can use '__FORWARD_KEY__' to advance before the audio finishes while reviewing."
            )
          );
          reviewToastShown = true;
        }
        reviewingInstructions = true;
      }
    }

    function next() {
      add_current_page_to_view_history();

      current_page++;

      // if done, finish up...
      if (current_page >= trial.pages.length) {
        endTrial();
      } else {
        show_current_page();
      }

      trial.on_page_change(current_page);
    }

    function back() {
      add_current_page_to_view_history();

      current_page--;

      show_current_page();

      trial.on_page_change(current_page);
    }

    function add_current_page_to_view_history() {
      var current_time = performance.now();

      var page_view_time = Math.round(current_time - last_page_update_time);

      view_history.push({
        page_index: current_page,
        viewing_time: page_view_time,
      });

      last_page_update_time = current_time;
    }

    const endTrial = () => {
      if (trial.allow_keys) {
        this.jsPsych.customKeyboardResponse.cancelKeyboardResponse(keyboard_listener);
      }

      if (dismissReviewToast) {
        dismissReviewToast();
        dismissReviewToast = null;
      }

      if (ambientAudioSource) {
        try { ambientAudioSource.stop(); } catch (e) {}
        ambientAudioSource = null;
      }
      if (ambientAudioCtx) {
        ambientAudioCtx.close();
        ambientAudioCtx = null;
      }

      // cleanup audio listeners
      try {
        if (currentAudioEl) {
          if (audioPlayHandler) currentAudioEl.removeEventListener("play", audioPlayHandler);
          if (audioPauseHandler) currentAudioEl.removeEventListener("pause", audioPauseHandler);
          if (audioEndedHandler) currentAudioEl.removeEventListener("ended", audioEndedHandler);
        }
      } catch (e) {
        console.warn("Failed to cleanup instruction audio listeners:", e);
      } finally {
        currentAudioEl = null;
        audioPlayHandler = audioPauseHandler = audioEndedHandler = null;
        audioIsPlaying = false;
      }

      var trial_data = {
        view_history: view_history,
        rt: Math.round(performance.now() - start_time),
      };

      this.jsPsych.finishTrial(trial_data);
    };

    const after_response = (info) => {
      // have to reinitialize this instead of letting it persist to prevent accidental skips of pages by holding down keys too long
      keyboard_listener = this.jsPsych.customKeyboardResponse.getKeyboardResponse({
        callback_function: after_response,
        valid_responses: [trial.key_forward, trial.key_backward, SECRET_SKIP_KEY],
        rt_method: "performance",
        persist: false,
        allow_held_key: false,
      });

      // Secret skip pressing the physical P key advances regardless of audio playing
      // Compare the keyCode (e.code) to SECRET_SKIP_KEY (e.g., 'KeyP')
      if (this.jsPsych.customKeyboardResponse.compareKeys(info.keyCode, SECRET_SKIP_KEY)) {
        next();
        return;
      }

      // check if key is forwards or backwards and update page
      if (this.jsPsych.customKeyboardResponse.compareKeys(info.keyCode, trial.key_backward)) {
        if (current_page !== 0 && trial.allow_backward) {
          back();
        }
      }

      if (this.jsPsych.customKeyboardResponse.compareKeys(info.keyCode, trial.key_forward)) {
        // During first pass, wait for audio; after reaching the final page once, allow review skips.
        if (reviewingInstructions || !audioIsPlaying) next();
      }
    };

    show_current_page();

    if (trial.allow_keys) {
      var keyboard_listener = this.jsPsych.customKeyboardResponse.getKeyboardResponse({
        callback_function: after_response,
        valid_responses: [trial.key_forward, trial.key_backward, SECRET_SKIP_KEY],
        rt_method: "performance",
        persist: false,
      });
    }
  }

  simulate(
    trial: TrialType<Info>,
    simulation_mode,
    simulation_options: any,
    load_callback: () => void
  ) {
    if (simulation_mode == "data-only") {
      load_callback();
      this.simulate_data_only(trial, simulation_options);
    }
    if (simulation_mode == "visual") {
      this.simulate_visual(trial, simulation_options, load_callback);
    }
  }

  private create_simulation_data(trial: TrialType<Info>, simulation_options) {
    let curr_page = 0;
    let rt = 0;
    let view_history = [];

    // if there is no view history and no RT, simulate a random walk through the pages
    if (!simulation_options.data?.view_history && !simulation_options.data?.rt) {
      while (curr_page !== trial.pages.length) {
        const view_time = Math.round(
          this.jsPsych.randomization.sampleExGaussian(3000, 300, 1 / 300)
        );
        view_history.push({ page_index: curr_page, viewing_time: view_time });
        rt += view_time;
        if (curr_page == 0 || !trial.allow_backward) {
          curr_page++;
        } else {
          if (this.jsPsych.randomization.sampleBernoulli(0.9) == 1) {
            curr_page++;
          } else {
            curr_page--;
          }
        }
      }
    }

    // if there is an RT but no view history, simulate a random walk through the pages
    // that ends on the final page when the RT is reached
    if (!simulation_options.data?.view_history && simulation_options.data?.rt) {
      rt = simulation_options.data.rt;
      while (curr_page !== trial.pages.length) {
        view_history.push({ page_index: curr_page, viewing_time: null });
        if (curr_page == 0 || !trial.allow_backward) {
          curr_page++;
        } else {
          if (this.jsPsych.randomization.sampleBernoulli(0.9) == 1) {
            curr_page++;
          } else {
            curr_page--;
          }
        }
      }
      const avg_rt_per_page = simulation_options.data.rt / view_history.length;
      let total_time = 0;
      for (const page of view_history) {
        const t = Math.round(
          this.jsPsych.randomization.sampleExGaussian(
            avg_rt_per_page,
            avg_rt_per_page / 10,
            1 / (avg_rt_per_page / 10)
          )
        );
        page.viewing_time = t;
        total_time += t;
      }
      const diff = simulation_options.data.rt - total_time;
      // remove equal diff from each page
      const diff_per_page = Math.round(diff / view_history.length);
      for (const page of view_history) {
        page.viewing_time += diff_per_page;
      }
    }

    // if there is a view history but no RT, make the RT equal the sum of the view history
    if (simulation_options.data?.view_history && !simulation_options.data?.rt) {
      view_history = simulation_options.data.view_history;
      rt = 0;
      for (const page of simulation_options.data.view_history) {
        rt += page.viewing_time;
      }
    }

    const default_data = {
      view_history: view_history,
      rt: rt,
    };

    const data = this.jsPsych.pluginAPI.mergeSimulationData(default_data, simulation_options);

    this.jsPsych.pluginAPI.ensureSimulationDataConsistency(trial, data);

    return data;
  }

  private simulate_data_only(trial: TrialType<Info>, simulation_options) {
    const data = this.create_simulation_data(trial, simulation_options);

    this.jsPsych.finishTrial(data);
  }

  private simulate_visual(trial: TrialType<Info>, simulation_options, load_callback: () => void) {
    const data = this.create_simulation_data(trial, simulation_options);

    const display_element = this.jsPsych.getDisplayElement();

    this.trial(display_element, trial);
    load_callback();

    const advance = (rt) => {
      if (trial.allow_keys) {
        this.jsPsych.pluginAPI.pressKey(trial.key_forward, rt);
      } else if (trial.show_clickable_nav) {
        this.jsPsych.pluginAPI.clickTarget(
          display_element.querySelector("#jspsych-instructions-next"),
          rt
        );
      }
    };

    const backup = (rt) => {
      if (trial.allow_keys) {
        this.jsPsych.pluginAPI.pressKey(trial.key_backward, rt);
      } else if (trial.show_clickable_nav) {
        this.jsPsych.pluginAPI.clickTarget(
          display_element.querySelector("#jspsych-instructions-back"),
          rt
        );
      }
    };

    let curr_page = 0;
    let t = 0;
    for (let i = 0; i < data.view_history.length; i++) {
      if (i == data.view_history.length - 1) {
        advance(t + data.view_history[i].viewing_time);
      } else {
        if (data.view_history[i + 1].page_index > curr_page) {
          advance(t + data.view_history[i].viewing_time);
        }
        if (data.view_history[i + 1].page_index < curr_page) {
          backup(t + data.view_history[i].viewing_time);
        }
        t += data.view_history[i].viewing_time;
        curr_page = data.view_history[i + 1].page_index;
      }
    }
  }
}

export default CustomInstructionsResponsePlugin;
